import {useCallback, useState, useRef} from 'react'
import {Button, ConfirmationDialog} from '@primer/react'
import {EyeClosedIcon} from '@primer/octicons-react'
import {marketplaceActionPath} from '@github-ui/paths'
import {useCSRFToken} from '@github-ui/use-csrf-token'
import {sendEvent} from '@github-ui/hydro-analytics'
import styles from '../../marketplace.module.css'
import type {ActionListing} from '@github-ui/marketplace-common'

interface DelistButtonProps {
  action: ActionListing
  repoAdminableByViewer: boolean
}

export function DelistButton(props: DelistButtonProps) {
  const {action, repoAdminableByViewer} = props

  const authToken = useCSRFToken(marketplaceActionPath({slug: action.slug ?? ''}), 'delete')

  const formRef = useRef<HTMLFormElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const onDialogClose = useCallback(() => setIsOpen(false), [])

  const onDialogConfirm = useCallback(() => {
    if (formRef.current) {
      sendEvent('marketplace.action.delist', {
        repository_action_id: action.globalRelayId,
        source_url: `${window.location}`,
        location: 'actions#show',
      })
      formRef.current.submit()
    }
  }, [action.globalRelayId])

  const handleDelistClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    // Ensure that form doesn't submit
    event.preventDefault()
    setIsOpen(true)
  }, [])

  return (
    <>
      {repoAdminableByViewer && action.slug && (
        <form
          ref={formRef}
          data-turbo="false"
          action={marketplaceActionPath({slug: action.slug})}
          method="post"
          data-testid="delist-form"
        >
          <input type="hidden" name="_method" value="delete" autoComplete="off" data-testid="hidden-delete" />
          <Button
            onClick={event => handleDelistClick(event)}
            data-testid="delist-button"
            variant="link"
            className={styles['marketplace-danger-link']}
          >
            <EyeClosedIcon size={16} className="mr-2" />
            Delist action
          </Button>
          {
            // eslint-disable-next-line github/authenticity-token
            <input type="hidden" name="authenticity_token" value={authToken} data-testid="hidden-authenticity-token" />
          }
          {isOpen && (
            <ConfirmationDialog
              title="Delist action?"
              onClose={gesture => (gesture === 'confirm' ? onDialogConfirm() : onDialogClose())}
              confirmButtonContent="Delist action"
              confirmButtonType="danger"
            >
              Are you sure you want to delist this action from the Marketplace? Note: It will still be installable as
              long as the repository is public.
            </ConfirmationDialog>
          )}
        </form>
      )}
    </>
  )
}

try{ DelistButton.displayName ||= 'DelistButton' } catch {}